// Cuerpo de un correo: el contenido NUEVO arriba y el HISTORIAL completo debajo,
// siempre visible (Galán 2026-09-25: el botón "Mostrar historial" estorbaba).
// Soporta texto y HTML (render en iframe sandbox, sin scripts, a su altura real).

import { useEffect, useMemo, useRef, useState } from 'react';

import type { MailMessage } from '../types';

// ── Heurísticas para separar "nuevo" vs "citado" ──────────────────────────
function splitText(text: string): { fresh: string; quoted: string } {
  const markers: RegExp[] = [
    /\n\s*El .+escribió:\s*\n/,               // es: "El ... escribió:"
    /\n\s*On .+wrote:\s*\n/,                    // en: "On ... wrote:"
    /\n-{2,}\s*Original Message\s*-{2,}/i,
    /\n-{2,}\s*Mensaje original\s*-{2,}/i,
    /\n_{5,}/,                                  // divisor Outlook
    /\n\s*De:\s.+\n\s*(Enviado|Sent|Fecha|Para):/i,
  ];
  let idx = -1;
  for (const re of markers) {
    const m = text.match(re);
    if (m && m.index !== undefined) idx = idx === -1 ? m.index : Math.min(idx, m.index);
  }
  const gt = text.match(/\n>.*(\n>.*)+/);       // bloque de líneas citadas ">"
  if (gt && gt.index !== undefined) idx = idx === -1 ? gt.index : Math.min(idx, gt.index);
  if (idx <= 0) return { fresh: text, quoted: '' };
  return { fresh: text.slice(0, idx).trimEnd(), quoted: text.slice(idx).trim() };
}

function splitHtml(html: string): { fresh: string; quoted: string } {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const sel = 'blockquote, .gmail_quote, .gmail_extra, .moz-cite-prefix, '
      + '.OutlookMessageHeader, #divRplyFwdMsg, #appendonsend';
    const q = doc.body.querySelector(sel);
    if (!q) return { fresh: html, quoted: '' };
    const quoted = q.outerHTML;
    q.remove();
    const fresh = doc.body.innerHTML;
    if (!fresh.replace(/<[^>]+>/g, '').trim()) return { fresh: html, quoted: '' }; // no vaciar todo
    return { fresh, quoted };
  } catch {
    return { fresh: html, quoted: '' };
  }
}

function HtmlFrame({ html, muted = false }: { html: string; muted?: boolean }) {
  // La altura se ajusta al contenido: un iframe NO crece solo, y con altura fija
  // un correo largo quedaba metido en una caja de ~240px ("se ve muy cortada").
  // sandbox sin allow-scripts: el HTML del correo no ejecuta nada; allow-same-origin
  // sólo deja que ESTA página mida el documento. allow-popups: los enlaces abren
  // en pestaña nueva (con sandbox="" no abrían).
  const ref = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(120);
  const doc = `<!doctype html><meta charset="utf-8"><base target="_blank">`
    + `<style>html,body{margin:0;overflow:hidden}body{font-family:system-ui,-apple-system,sans-serif;font-size:15px;`
    + `line-height:1.6;color:${muted ? '#555' : '#111'};word-wrap:break-word;overflow-wrap:anywhere}`
    + `img{max-width:100%;height:auto}table{max-width:100%}`
    + `a{color:#2563eb}blockquote{margin:0 0 0 .8em;padding-left:.8em;border-left:2px solid #ddd;color:#555}</style>`
    + `<div style="padding:2px 4px">${html}</div>`;

  useEffect(() => {
    const frame = ref.current;
    if (!frame) return;
    let ro: ResizeObserver | null = null;
    const measure = () => {
      const d = frame.contentDocument;
      if (d?.body) setHeight(Math.max(40, d.documentElement.scrollHeight));
    };
    const onLoad = () => {
      measure();
      const d = frame.contentDocument;
      if (!d?.body) return;
      ro = new ResizeObserver(measure);          // imágenes que cargan después
      ro.observe(d.body);
    };
    frame.addEventListener('load', onLoad);
    if (frame.contentDocument?.readyState === 'complete') onLoad();
    return () => { frame.removeEventListener('load', onLoad); ro?.disconnect(); };
  }, [html]);

  return (
    <iframe
      ref={ref}
      title="mensaje"
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      className="w-full rounded-lg border-0 bg-white"
      style={{ height }}
      srcDoc={doc}
    />
  );
}

export function MessageBody({ message }: { message: MailMessage }) {
  // Historial SIEMPRE visible, completo (pedido del cliente: nada de "Mostrar
  // historial"). Se conserva la separación visual entre lo nuevo y lo citado.
  const isHtml = Boolean(message.body_html);
  const { fresh, quoted } = useMemo(
    () => (isHtml ? splitHtml(message.body_html) : splitText(message.body_text || '')),
    [message.uid, isHtml, message.body_html, message.body_text],
  );

  return (
    <div className="space-y-3">
      {isHtml ? (
        <HtmlFrame html={fresh} />
      ) : (
        <pre className="whitespace-pre-wrap break-words font-sans text-[15px] leading-relaxed text-foreground/90">
          {fresh || '(sin contenido)'}
        </pre>
      )}

      {quoted && (
        <div>
          <div className="mb-2 text-xs font-medium text-muted-foreground">Historial de la conversación</div>
          <div className="rounded-lg border-l-2 border-border pl-3">
            {isHtml ? (
              <HtmlFrame html={quoted} muted />
            ) : (
              <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-relaxed text-muted-foreground">
                {quoted}
              </pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
